<script lang="ts" setup>

//vue
import { onMounted, reactive, computed } from "vue";
import { useRoute } from 'vue-router';

//servicos
import config from "@/app.config.json";
import * as service from "@/services/relatoriosService";
import { fdata, f2dec, fdataUS } from "@/utils/funcoes";
import { useOrdenacao } from "@/composables/useOrdenacao";

//componentes
import BreadCrumb from "../../components/Common/BreadCrumb.vue";
import TextBox from "@/components/TextBox/Index.vue";
import TrataTecla from "@/components/TrataTecla/Index.vue";
import Notificacao from "@/components/Notificacao/Index.vue";
import AutoCompleteCidade from "@/components/AutoCompletes/AutoCompleteCidade.vue";
import DropdownCidadesAtiva from "@/components/Dropdowns/DropdownCidadesAtiva.vue";
import ReportView from "@/components/ReportView/Index.vue";
import ReportCabecalho from "@/components/ReportView/Cabecalho.vue";

const route = useRoute();

var controle = reactive({

  filtros: {
    dataVencInicial: fdataUS(new Date(new Date().setDate(new Date().getDate() - 30))),
    dataVencFinal: fdataUS(new Date()),
    status: "Recebido",
  } as unknown as service.FiltroMensalidades,
  cidadeId: null as number | null,
  cidadeNome: "" as string,
  itens: [] as service.RelMensDTO[],

  totalRegistros: 0,

  qtdeRegistros: 0,
  qtdePagos: 0,
  qtdeEmAberto: 0,
  qtdeVencidos: 0,

  totalGeral: 0,
  totalPago: 0,
  totalEmAberto: 0,
  totalVencido: 0,
  totalAcresDesc: 0,

  erro: "",
  status: "",
  pesquisando: false,
})


function selecionouCidade(cidade: any) {
  controle.cidadeNome = cidade ? `${cidade.nome} - ${cidade.estado}` : "";
}

const resumoFiltros = computed(() => {
  const statusLabel = controle.filtros.status === "Recebido" ? "Recebido"
    : controle.filtros.status === "A Receber" ? "Em Aberto"
    : "Todos";
  const partes = [
    `Vencto: ${fdata(controle.filtros.dataVencInicial)} a ${fdata(controle.filtros.dataVencFinal)}`,
    `Cidade: ${controle.cidadeId ? controle.cidadeNome : "Todas"}`,
    `Status: ${statusLabel}`,
  ];
  return partes.join("  |  ");
});

async function pesquisar() {

  if (controle.cidadeId != null && controle.cidadeId > 0) {
    controle.filtros.cidadesIds = [controle.cidadeId];
  } else {
    controle.filtros.cidadesIds = null;
  }

  service.mensalidades(controle.filtros)
    .then(r => {
      controle.itens = r.dados;

      controle.totalRegistros = r.totalRegistros;
      controle.pesquisando = false;

      controle.qtdeRegistros = controle.itens.length;
      controle.qtdeEmAberto = 0;
      controle.qtdePagos = 0;
      controle.qtdeVencidos = 0;

      controle.totalGeral = 0;
      controle.totalEmAberto = 0;
      controle.totalPago = 0;
      controle.totalVencido = 0;
      controle.totalAcresDesc = 0;

      controle.itens.forEach(a => {

        controle.totalGeral += a.valor;
        
        if (a.dataPagamento != null) {
          controle.qtdePagos += 1;
          controle.totalPago += a.valorPago;
          controle.totalAcresDesc += a.valorPago - a.valor;
        } 

      });

    })
    .catch(erro => {
      controle.erro = erro;
      controle.pesquisando = false;
    })
}

const sort = useOrdenacao("dataPagamento");

const itensOrdenados = computed(() => sort.aplicar(controle.itens, {
  nome: (item: service.RelMensDTO) => item.clienteNome || "",
  cliente: (item: service.RelMensDTO) => item.clienteId ?? -1,
  parcela: (item: service.RelMensDTO) => item.parcela ?? -1,
  valor: (item: service.RelMensDTO) => item.valor ?? 0,
  valorPago: (item: service.RelMensDTO) => item.valorPago ?? 0,
  acresDesc: (item: service.RelMensDTO) => (item.valorPago > 0 ? item.valorPago - item.valor : 0),
  dataVencimento: (item: service.RelMensDTO) => item.dataVencimento || "",
  dataPagamento: (item: service.RelMensDTO) => item.dataPagamento || "",
}));

</script>

<template>
  <BreadCrumb PageTitle="Relatório de Mensalidades" :items="[
    { label: 'Relatorios', to: '/relatorios' },
    { label: 'Mensalidades', to: '/relatorios/mensalidades' }]" />

  <Notificacao :erro="controle.erro" @limparErro="controle.erro = ''" :progresso="controle.status"
    @limparProgresso="controle.status = ''" />

  <TrataTecla>
    <div class="card mb-2 d-print-none">
      <div class="card-body">
        <div class="row">

          <!-- coluna 1-->
          <div class="col-md-12">
            <div class="row mb-2">

              <div class="col-md-2 col-6">
                <label>Vencto Inicial</label>
                <input v-model="controle.filtros.dataVencInicial" class="form-control form-control-sm"
                  id="dataVencInicial" :type="'date'" />
              </div>

              <div class="col-md-2 col-6">
                <label>Vencto Final</label>
                <input v-model="controle.filtros.dataVencFinal" class="form-control form-control-sm" id="dataVencFinal"
                  :type="'date'" />
              </div>

              <div class="col-md-2 col-6">
                <label>Cidade</label>
                <DropdownCidadesAtiva v-model="controle.cidadeId" id="cidadeId" @selected="selecionouCidade" />
              </div>

              <div class="col-md-2 col-6">
                <label>Status</label>
                <select v-model="controle.filtros.status" class="form-select form-select-sm" id="status">
                  <option value="">Todos</option>
                  <option :value="'Recebido'">Recebido</option>
                  <option :value="'A Receber'">Em Aberto</option>
                </select>
              </div>

              <div class="col-md-2 col-6">
                <a class="btn btn-primary btn-sm mt-4" @click="pesquisar">Pesquisar</a>

                <RouterLink class="btn btn-sm btn-secondary float-end mt-4" :to="'/parceiros'">Fechar</RouterLink>
              </div>

            </div>
          </div>

        </div>
      </div>

    </div>


    <div class="col-md-12">

      <ReportView :titulo="'Rel Mensalidades'" :btnImprimir="true" > 
      <div v-if="controle.erro != ''" class="col-md-12 text-center">
        <div class="p-4">
          <div class="fw-14 text-danger"> {{ controle.erro }}</div>
        </div>
      </div>

      <div v-if="controle.erro == '' && controle.pesquisando == true" class="col-md-12 text-center">
        <div class="p-4">
          <i class="fa fa-cog fa-2x fa-spin"></i>
          <div class="fw-16">Pesquisando...</div>
        </div>
      </div>

      <template v-if="controle.erro == '' && controle.pesquisando == false">

        <ReportCabecalho :Titulo="'Relatório de Mensalidades'" :Filtros="resumoFiltros" />

        <table class="tabela table-sm table-bordered f12px" id="tabela-relatorio">
          <thead>
            <tr class="table-relatorio">
              <th class="text-start text-truncate colNome col-ordenavel" @click="sort.alternar('nome')">Nome <i :class="sort.classeIcone('nome')"></i></th>
              <th class="text-end col-ordenavel" @click="sort.alternar('cliente')">Cliente <i :class="sort.classeIcone('cliente')"></i></th>
              <th class="text-center col-ordenavel" @click="sort.alternar('parcela')">Parcela <br> Contrato <i :class="sort.classeIcone('parcela')"></i></th>
              <th class="text-end col-ordenavel" @click="sort.alternar('valor')">Valor R$ <i :class="sort.classeIcone('valor')"></i></th>
              <th class="text-end col-ordenavel" @click="sort.alternar('valorPago')">Pago R$ <i :class="sort.classeIcone('valorPago')"></i></th>
              <th class="text-end col-ordenavel" @click="sort.alternar('acresDesc')">Acres<br>Desc R$ <i :class="sort.classeIcone('acresDesc')"></i></th>
              <th class="text-center col-ordenavel" @click="sort.alternar('dataVencimento')">Vencimento <i :class="sort.classeIcone('dataVencimento')"></i></th>
              <th class="text-center col-ordenavel" @click="sort.alternar('dataPagamento')">Pagamento <i :class="sort.classeIcone('dataPagamento')"></i></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, idx) in itensOrdenados" :key="idx">
              <td class="text-start text-truncate colNome">{{ item.clienteNome }}</td>
              <td class="text-end">{{ item.clienteId }}</td>
              <td class="text-center">{{ item.parcela }} / {{ item.contratoId }}</td>
              <td class="text-end">{{ f2dec(item.valor) }}</td>
              <td class="text-end">{{ f2dec(item.valorPago) }}</td>
               <td class="text-end">{{ f2dec(item.valorPago > 0 ? item.valorPago - item.valor : 0) }}</td>
              <td class="text-center">{{ fdata(item.dataVencimento) }}</td>
              <td class="text-center">{{ fdata(item.dataPagamento) }}</td>
            </tr>
            <tr class="border-top">
              <td></td>
              <td></td>
              <td></td>
              <td class="text-end fw-bold" >{{ f2dec(controle.totalGeral) }}</td>
              <td class="text-end fw-bold" >{{ f2dec(controle.totalPago) }}</td>
              <td class="text-end fw-bold" >{{ f2dec(controle.totalAcresDesc) }}</td>
              <td></td>
              <td></td>
            </tr>
          </tbody>

        </table>

      </template>
    </ReportView>
    
    </div>
    


  </TrataTecla>

</template>

<style lang="scss" scoped>

  .colNome {
    width: 250px;
  }

</style>